/*
 * Shared foundation for the Oct 2026 redesign, Docs/Tasks/Mobile_Redesign/.
 * Loaded first by pbs-r-assets, after pbs-quick-add.js; frozen after gate-01.
 * One IIFE, delegated events, no dependencies or page-wide MutationObserver.
 *
 * C1: PBSPhone.mq / isPhone(). C2: overlay.init(root?), open(name, { trigger,
 * stack }) -> boolean, close() closes the top only. Desktop is opt-in through
 * data-pbs-desktop on the root; absent/missing overlays return false, preserving
 * link fallbacks. No registry overlay opts in at the gate. Desktop product size
 * guide is inline; Quick add, bag and Saved retain their own scripts (C10).
 * C7: pbs:overlay:open / close on document, detail { name, overlay, trigger }.
 * C8: init on shopify:section:load (including detail { pbs: true }); replace the
 * stale body-level instance when Section Rendering supplies the same name.
 * C11: dialog roots use data-pbs-overlay, aria-labelledby, hidden; close controls
 * use data-pbs-close, initial focus data-pbs-autofocus. init reveals matching
 * data-pbs-open triggers and adds html.pbs-has-NAME. One extra stacked layer.
 *
 * Provider APIs C3-C6 live in pbs-r-12.js, pbs-r-13.js, pbs-r-15.js.
 * C9: Dawn subscribe(PUB_SUB_EVENTS.cartUpdate, cb), cart-drawer.open()/close().
 * C12: Saved data-pbs-open-saved, data-pbs-saved-count/data-n, data-pbs-heart (15).
 * C13: a[data-pbs-qa-open] inside [data-pbs-card], data-pbs-qa-size,
 * .pbs-qa__guide[data-pbs-garment] (14). C14: data-pbs-sg-open and
 * a[href="#size-guide"] (11). C15: #cart-icon-bubble / #CartDrawer unchanged;
 * tr.cart-item[data-variant-id], .pbs-just (13).
 * C25: localStorage pbs_saved (id strings, podbysoni-brand.js), pbs_saved_handles
 * ({ id: handle }, podbysoni-header.js); sessionStorage pbs-sg-unit (in/cm, 12).
 * EVERY storage read/write belongs in try/catch. This core uses no storage.
 * C26: /products/HANDLE?section_id=pbs-saved-item (15); collection
 * ?sections=BAR,GRID,FOOT from pbs-colm-bar[data-section-id], #product-grid[data-id],
 * .pbs-colm-foot[data-section-id] (09/10), sheets also data-section-id;
 * Dawn cart-drawer + cart-icon-bubble and predictive-search (04).
 * C27: pbs-predictive-search (04), pbs-colm-bar (09), pbs-story-m (07): one owner
 * each, customElements.get guards; existing custom elements are not redefined.
 * C28: owners replace their pbs-r-NN.* stubs; this file stays shared and frozen.
 */
(function () {
  'use strict';

  if (window.PBSPhone && window.PBSPhone.overlay) return;

  var mq = window.matchMedia('(max-width: 749px)');
  var PBSPhone = window.PBSPhone = window.PBSPhone || {};
  PBSPhone.mq = mq;
  PBSPhone.isPhone = function () { return mq.matches; };

  // C10: provider-owned ids and kinds; custom names also work for smoke tests.
  var REGISTRY = Object.freeze({
    menu: { id: 'pbs-menu', kind: 'pbs-drawer pbs-drawer--left', owner: '03' },
    search: { id: 'pbs-search', kind: 'pbs-drop', owner: '04' },
    filter: { id: 'pbs-filter', kind: 'pbs-sheet', owner: '10' },
    sort: { id: 'pbs-sort', kind: 'pbs-sheet', owner: '10' },
    'size-guide': { id: 'pbs-size-guide', kind: 'pbs-sheet', owner: '12' }
  });
  var overlays = new Map();
  var closing = new Map();
  var stack = [];
  var backdrop = null;
  var gesture = null;
  var FOCUSABLE = 'a[href], area[href], button, input:not([type="hidden"]), select, textarea, summary, [tabindex], [contenteditable="true"]';

  function top() { return stack[stack.length - 1]; }

  function setInert(node, value) {
    node.inert = value;
    if (value) node.setAttribute('inert', '');
    else node.removeAttribute('inert');
  }

  function focusable(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (node) {
      return !node.matches(':disabled') && node.tabIndex >= 0 && !node.closest('[hidden], [inert], [aria-hidden="true"]')
        && node.getClientRects().length > 0 && window.getComputedStyle(node).visibility !== 'hidden';
    });
  }

  function initialFocus(root) {
    var node = root.querySelector('[data-pbs-autofocus]')
      || root.querySelector('.pbs-sheet__title, h1, h2, h3') || root;
    if (!node.matches(FOCUSABLE)) node.setAttribute('tabindex', '-1');
    return node;
  }

  function focusEntry(entry, preferred) {
    if (!entry || !entry.overlay.isConnected) return;
    var target = preferred && entry.overlay.contains(preferred) && !preferred.disabled
      ? preferred : initialFocus(entry.overlay);
    if (typeof window.trapFocus === 'function') window.trapFocus(entry.overlay, target);
    else target.focus({ preventScroll: true });
  }

  function releaseFocus(target) {
    target = target && target.isConnected && !target.closest('[hidden], [inert]') ? target : null;
    if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(target);
    else if (target && target.focus) target.focus({ preventScroll: true });
  }

  function eventFor(type, entry) {
    document.dispatchEvent(new CustomEvent('pbs:overlay:' + type, {
      detail: { name: entry.name, overlay: entry.overlay, trigger: entry.trigger }
    }));
  }

  function expanded(name, value, trigger) {
    document.querySelectorAll('[data-pbs-open]').forEach(function (node) {
      if (node.getAttribute('data-pbs-open') === name) node.setAttribute('aria-expanded', String(value));
    });
    if (trigger && trigger.setAttribute) trigger.setAttribute('aria-expanded', String(value));
  }

  function ensureBackdrop() {
    if (backdrop && backdrop.isConnected) return;
    backdrop = document.querySelector('[data-pbs-backdrop]');
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = 'pbs-backdrop';
      backdrop.setAttribute('data-pbs-backdrop', '');
    }
    backdrop.setAttribute('aria-hidden', 'true');
    if (backdrop.parentNode !== document.body) document.body.appendChild(backdrop);
  }

  function sync() {
    var active = top();
    document.documentElement.classList.toggle('pbs-ov-lock', !!active);
    document.body.classList.toggle('pbs-overflow-hidden', !!active);
    if (backdrop) {
      backdrop.classList.toggle('is-on', !!active && active.ready);
      backdrop.classList.toggle('is-stacked', !!active && active.stacked);
      backdrop.toggleAttribute('data-pbs-desktop', !!active && active.overlay.hasAttribute('data-pbs-desktop'));
    }
    stack.forEach(function (entry) {
      var covered = entry !== active;
      setInert(entry.overlay, covered);
      if (covered) entry.overlay.setAttribute('aria-hidden', 'true');
      else entry.overlay.removeAttribute('aria-hidden');
    });
  }

  function cancelGesture() {
    if (!gesture) return;
    gesture.overlay.style.removeProperty('transition');
    gesture.overlay.style.removeProperty('transform');
    gesture = null;
  }

  function hide(entry) {
    entry.overlay.hidden = true;
    setInert(entry.overlay, true);
    entry.overlay.setAttribute('aria-hidden', 'true');
    entry.overlay.classList.remove('is-stacked');
    closing.delete(entry.overlay);
  }

  function dismiss(entry, immediate, restoreFocus) {
    var index = stack.indexOf(entry);
    if (index === -1) return;
    var wasTop = entry === top();
    stack.splice(index, 1);
    entry.active = false;
    if (gesture && gesture.overlay === entry.overlay) cancelGesture();
    entry.overlay.classList.remove('is-open');
    setInert(entry.overlay, true);
    entry.overlay.setAttribute('aria-hidden', 'true');
    expanded(entry.name, false, entry.trigger);
    if (immediate) hide(entry);
    else {
      var duration = !mq.matches && entry.overlay.classList.contains('pbs-drawer') ? 550 : 380;
      closing.set(entry.overlay, { entry: entry, timer: window.setTimeout(function () { hide(entry); }, duration) });
    }
    sync();
    if (wasTop) {
      releaseFocus(restoreFocus ? entry.trigger : null);
      if (top()) focusEntry(top(), restoreFocus ? entry.trigger : null);
    }
    eventFor('close', entry);
  }

  function close() {
    if (top()) dismiss(top(), false, true);
  }

  function closeAll(immediate) {
    stack.slice().reverse().forEach(function (entry) { dismiss(entry, immediate, false); });
    if (immediate) {
      closing.forEach(function (pending) { window.clearTimeout(pending.timer); hide(pending.entry); });
    }
    sync();
  }

  function init(root) {
    if (!document.body) return;
    root = root && root.querySelectorAll ? root : document;
    ensureBackdrop();
    var candidates = [];
    if (root.nodeType === 1 && root.matches('[data-pbs-overlay]')) candidates.push(root);
    candidates = candidates.concat(Array.prototype.slice.call(root.querySelectorAll('[data-pbs-overlay]')));
    // Nested replacements win over body-level stale copies, regardless of DOM order.
    candidates.sort(function (a, b) {
      return Number(a.parentNode !== document.body) - Number(b.parentNode !== document.body);
    });
    candidates.forEach(function (overlay) {
      var name = overlay.getAttribute('data-pbs-overlay');
      if (!name || !/^[a-z0-9-]+$/i.test(name)) return;
      var stale = overlays.get(name);
      if (stale && stale !== overlay) {
        stack.slice().forEach(function (entry) {
          if (entry.overlay === stale) dismiss(entry, true, false);
        });
        var pending = closing.get(stale);
        if (pending) { window.clearTimeout(pending.timer); closing.delete(stale); }
        stale.remove();
      }
      overlays.set(name, overlay);
      if (!overlay.id && REGISTRY[name]) overlay.id = REGISTRY[name].id;
      if (overlay.parentNode !== document.body) document.body.appendChild(overlay);
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      if (!stack.some(function (entry) { return entry.overlay === overlay; }) && !closing.has(overlay)) {
        overlay.classList.remove('is-open', 'is-stacked');
        hide({ overlay: overlay });
      }
    });
    overlays.forEach(function (overlay, name) {
      if (!overlay.isConnected) {
        stack.slice().forEach(function (entry) {
          if (entry.overlay === overlay) dismiss(entry, true, false);
        });
        overlays.delete(name);
        document.documentElement.classList.remove('pbs-has-' + name);
        return;
      }
      document.documentElement.classList.add('pbs-has-' + name);
      document.querySelectorAll('[data-pbs-open]').forEach(function (trigger) {
        if (trigger.getAttribute('data-pbs-open') !== name) return;
        trigger.hidden = false;
        trigger.setAttribute('aria-expanded', String(stack.some(function (entry) { return entry.name === name; })));
      });
    });
    sync();
  }

  function open(name, opts) {
    opts = opts || {};
    init();
    var overlay = overlays.get(name);
    if (!overlay || (!mq.matches && !overlay.hasAttribute('data-pbs-desktop'))) return false;
    var current = stack.find(function (entry) { return entry.overlay === overlay; });
    if (current) {
      if (current === top() && current.ready) focusEntry(current);
      return true;
    }
    if (opts.stack === true && top() && top().stacked) return false;
    if (opts.stack !== true) closeAll(false);
    var pending = closing.get(overlay);
    if (pending) { window.clearTimeout(pending.timer); closing.delete(overlay); }
    var trigger = opts.trigger || document.activeElement;
    if (trigger === document.body || trigger === document.documentElement) trigger = null;
    var entry = {
      name: name, overlay: overlay, trigger: trigger,
      stacked: opts.stack === true, ready: false, active: true
    };
    stack.push(entry);
    overlay.hidden = false;
    overlay.classList.remove('is-open');
    overlay.classList.toggle('is-stacked', entry.stacked);
    overlay.style.removeProperty('transform');
    overlay.style.removeProperty('transition');
    expanded(name, true, entry.trigger);
    sync();
    // Safari requires two frames between removing hidden and beginning the slide.
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        if (!entry.active) return;
        entry.ready = true;
        overlay.classList.add('is-open');
        sync();
        if (entry === top()) focusEntry(entry);
      });
    });
    eventFor('open', entry);
    return true;
  }

  PBSPhone.overlay = { init: init, open: open, close: close };

  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) return;
    var trigger = event.target.closest('[data-pbs-open]');
    if (trigger) {
      if (open(trigger.getAttribute('data-pbs-open'), { trigger: trigger })) event.preventDefault();
      return;
    }
    var closer = event.target.closest('[data-pbs-close]');
    if (top() && ((closer && top().overlay.contains(closer)) || event.target === backdrop)) {
      event.preventDefault();
      close();
    }
  });

  // Capture prevents an underlying Quick add key handler closing both layers.
  // Dawn still establishes the active focus trap; this also handles heading
  // autofocus and dynamically changed controls at the edges of that trap.
  document.addEventListener('keydown', function (event) {
    var entry = top();
    if (!entry) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      close();
    } else if (event.key === 'Tab') {
      event.stopImmediatePropagation();
      var items = focusable(entry.overlay);
      var here = document.activeElement;
      var index = items.indexOf(here);
      if (!items.length || index === -1 || (!event.shiftKey && index === items.length - 1) || (event.shiftKey && index === 0)) {
        event.preventDefault();
        (items.length ? items[event.shiftKey ? items.length - 1 : 0] : initialFocus(entry.overlay)).focus();
      }
    }
  }, true);

  document.addEventListener('focusin', function (event) {
    var entry = top();
    if (entry && entry.ready && !entry.overlay.contains(event.target)) focusEntry(entry);
  });

  document.addEventListener('pointerdown', function (event) {
    var entry = top();
    if (!mq.matches || !entry || !entry.ready || !entry.overlay.classList.contains('pbs-sheet')
      || (event.button !== undefined && event.button !== 0) || !(event.target instanceof Element)) return;
    var handle = event.target.closest('.pbs-sheet__grab, .pbs-sheet__head');
    if (!handle || !entry.overlay.contains(handle)
      || event.target.closest('button, a, input, select, textarea, summary, [role="button"]')) return;
    gesture = { overlay: entry.overlay, handle: handle, id: event.pointerId, y: event.clientY,
      dy: 0, lastY: event.clientY, lastTime: event.timeStamp, velocity: 0 };
    entry.overlay.style.transition = 'none';
    try { handle.setPointerCapture(event.pointerId); } catch (error) { /* Detached handle: no capture. */ }
    event.preventDefault();
  });

  document.addEventListener('pointermove', function (event) {
    if (!gesture || gesture.id !== event.pointerId) return;
    var elapsed = event.timeStamp - gesture.lastTime;
    if (elapsed > 0) gesture.velocity = (event.clientY - gesture.lastY) / elapsed;
    gesture.lastY = event.clientY;
    gesture.lastTime = event.timeStamp;
    gesture.dy = Math.max(0, event.clientY - gesture.y);
    gesture.overlay.style.transform = 'translateY(' + gesture.dy + 'px)';
    event.preventDefault();
  }, { passive: false });

  function endGesture(event) {
    if (!gesture || gesture.id !== event.pointerId) return;
    var dragged = gesture;
    var dy = Math.max(0, event.clientY - dragged.y);
    var velocity = event.timeStamp - dragged.lastTime > 80 ? 0 : dragged.velocity;
    var shouldClose = event.type === 'pointerup'
      && (dy > dragged.overlay.getBoundingClientRect().height * 0.25 || (dy > 0 && velocity > 0.5));
    cancelGesture();
    if (shouldClose) close();
  }
  document.addEventListener('pointerup', endGesture);
  document.addEventListener('pointercancel', endGesture);
  document.addEventListener('lostpointercapture', function () { cancelGesture(); });

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  window.addEventListener('pageshow', function () { closeAll(true); });
  function onBreakpoint() {
    cancelGesture();
    if (!mq.matches) {
      stack.slice().reverse().forEach(function (entry) {
        if (!entry.overlay.hasAttribute('data-pbs-desktop')) dismiss(entry, true, false);
      });
      if (top()) focusEntry(top());
    }
  }
  if (mq.addEventListener) mq.addEventListener('change', onBreakpoint);
  else mq.addListener(onBreakpoint);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); }, { once: true });
  else init();
}());
